import * as React from 'react';
import { useCustomEventEffect } from '../../hooks';
import { sanitizeContent } from '../../thumbtack';
import { HiddenInputs } from './shared/HiddenInputs';
import {
  IAnswerMatchParams,
  IQuestionAnswerUpdateEventDetails,
  IQuestionDetails,
  matchQuestionConditions,
} from './shared/utilities';

interface IProps {
  initialValue: boolean;
  translations: { yes: string; no: string; day: string; month: string; year: string; documentLabelText: string };
  questionDetails: IQuestionDetails;
  conditionalQuestionParentQuestionId: string;
  conditionalQuestionConditions: { operator: string; value: string }[];
  formResourceType: 'application' | 'job_seeker';
}

export default function Boolean(props: IProps) {
  const {
    initialValue,
    translations,
    questionDetails,
    conditionalQuestionConditions,
    conditionalQuestionParentQuestionId,
    formResourceType,
  } = props;
  const { index, questionId, questionType, title, additionalInformation, required } = questionDetails;
  const condition = conditionalQuestionConditions && conditionalQuestionConditions[0];
  const [toggledYes, setToggledYes] = React.useState<boolean>(initialValue === true);
  const [toggledNo, setToggledNo] = React.useState<boolean>(initialValue === false);
  const [visible, setVisible] = React.useState<boolean>(conditionalQuestionParentQuestionId === null);

  /**
   * Sender - announce to possible conditional question answers when the value of this rendered component changes
   */
  const { dispatch: announceAnswerUpdate } = useCustomEventEffect(`question:${questionId}:answer:update`);

  React.useEffect(() => {
    setTimeout(() => {
      announceAnswerUpdate({ questionType: 'boolean', value: initialValue });
    }, 250);
  }, []);

  /**
   * Receiver - receive announcements from possible parent conditional questions about their changing value(s) so
   * we know whether to hide or show this answer component
   */
  useCustomEventEffect(
    `question:${conditionalQuestionParentQuestionId}:answer:update`,
    ({ detail }: CustomEvent<IQuestionAnswerUpdateEventDetails>) => {
      const matchParams: IAnswerMatchParams = {
        questionType: detail.questionType,
        value: detail.value,
        parentQuestionCondition: condition,
        maxNumAnswerOptions: detail.maxNumAnswerOptions,
      };
      // Only hide element if it's got a parent conditional question
      if (conditionalQuestionParentQuestionId) {
        setVisible(matchQuestionConditions(matchParams));
      }
    },
  );

  const onChangeYes = (event: React.FormEvent<HTMLInputElement>) => {
    if (event.currentTarget.checked) {
      setToggledYes(true);
      setToggledNo(false);
      announceAnswerUpdate({ questionType: 'boolean', value: true });
    }
  };

  const onChangeNo = (event: React.FormEvent<HTMLInputElement>) => {
    if (event.currentTarget.checked) {
      setToggledYes(false);
      setToggledNo(true);
      announceAnswerUpdate({ questionType: 'boolean', value: false });
    }
  };

  if (!visible) return null;

  return (
    <div className="pad-v-3">
      <label
        className="external-form__label"
        htmlFor={`${formResourceType}_form_${formResourceType}_answers_attributes_${index}_boolean_answer`}
      >
        <span className={`external-form__label--title ${required ? 'external-form__label--required' : ''}`}>
          {title}
        </span>
        {additionalInformation && <span dangerouslySetInnerHTML={{ __html: sanitizeContent(additionalInformation) }} />}
      </label>
      <div className="frow frow--gutters frow--gutters-sm-2x">
        <div className="col-1-2">
          <div className="checkable-input checkable-input--themed checkable-input--full">
            <div style={{ display: 'none' }} id={`answer-label-${index}-true`}>
              {title} yes
            </div>

            <input
              onChange={onChangeYes}
              className="checkable-input__input"
              type="radio"
              id={`${formResourceType}_form_${formResourceType}_answers_attributes_${index}_boolean_answer_true`}
              name={`${formResourceType}_form[${formResourceType}][answers_attributes][${index}][boolean_answer]`}
              value="true"
              defaultChecked={toggledYes}
              tabIndex={0}
              aria-labelledby={`answer-label-${index}-true`}
            />
            <label
              aria-labelledby={`answer-label-${index}-true`}
              className="checkable-input__label"
              htmlFor={`${formResourceType}_form_${formResourceType}_answers_attributes_${index}_boolean_answer_true`}
            >
              {translations.yes}
            </label>
          </div>
        </div>

        <div className="col-1-2">
          <div className="checkable-input checkable-input--themed checkable-input--full">
            <div style={{ display: 'none' }} id={`answer-label-${index}-false`}>
              {title} no
            </div>

            <input
              onChange={onChangeNo}
              className="checkable-input__input"
              type="radio"
              id={`${formResourceType}_form_${formResourceType}_answers_attributes_${index}_boolean_answer_false`}
              name={`${formResourceType}_form[${formResourceType}][answers_attributes][${index}][boolean_answer]`}
              value="false"
              tabIndex={0}
              defaultChecked={toggledNo}
              aria-labelledby={`answer-label-${index}-false`}
            />
            <label
              aria-labelledby={`answer-label-${index}-false`}
              className="checkable-input__label"
              htmlFor={`${formResourceType}_form_${formResourceType}_answers_attributes_${index}_boolean_answer_false`}
            >
              {translations.no}
            </label>
          </div>
        </div>
      </div>
      <HiddenInputs
        formResourceType={formResourceType}
        index={index}
        additionalInformation={additionalInformation}
        title={title}
        questionId={questionId}
        questionType={questionType}
      />
    </div>
  );
}
